import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AgentTemplate, CustomFieldDef, Agent, Workspace } from '../types';
import { getTemplate, updateTemplate, deleteTemplate, listAgents, listWorkspaces, updateWorkspace } from '../api/client';

const LLM_MODELS = [
  'gpt-5.6-sol', 'gpt-5.6-terra', 'gpt-5.6-luna',
  'gpt-5.4', 'gpt-5.4-mini', 'gpt-5.4-nano',
  'gpt-4.1', 'gpt-4.1-mini', 'gpt-4.1-nano', 'gpt-4o', 'gpt-4o-mini',
  'claude-opus-4-8', 'claude-sonnet-5', 'claude-sonnet-4-6', 'claude-haiku-4-5',
];
const STT_OPTIONS = [
  { value: 'deepgram-flux', label: 'Deepgram Flux' },
  { value: 'deepgram-nova-3', label: 'Deepgram Nova-3' },
  { value: 'gladia', label: 'Gladia STT' },
  { value: 'webSpeech', label: 'Web Speech API' },
];
const TTS_OPTIONS = [
  { value: 'openai-tts-1', label: 'OpenAI TTS-1' },
  { value: 'cartesia-sonic-3', label: 'Cartesia Sonic-3' },
  { value: 'elevenlabs-multilingual-v2', label: 'ElevenLabs Multilingual v2' },
  { value: 'browserTts', label: 'Browser TTS' },
];
const LANGUAGES = [
  { value: 'en', label: 'English' },
  { value: 'es', label: 'Spanish' },
  { value: 'he', label: 'Hebrew' },
  { value: 'ru', label: 'Russian' },
];

export function TemplateDetail() {
  const { templateId } = useParams<{ templateId: string }>();
  const navigate = useNavigate();

  const [template, setTemplate] = useState<AgentTemplate | null>(null);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [greeting, setGreeting] = useState('');
  const [sttProvider, setSttProvider] = useState('');
  const [ttsProvider, setTtsProvider] = useState('');
  const [llmModel, setLlmModel] = useState('');
  const [baseSystemPrompt, setBaseSystemPrompt] = useState('');
  const [language, setLanguage] = useState('');
  const [voiceMode, setVoiceMode] = useState<'pipeline' | 'realtime'>(
    'pipeline',
  );
  const [isPublic, setIsPublic] = useState(true);
  const [customFields, setCustomFields] = useState<CustomFieldDef[]>([]);
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [selectedWorkspaceIds, setSelectedWorkspaceIds] = useState<string[]>([]);
  const [initialWorkspaceIds, setInitialWorkspaceIds] = useState<string[]>([]);

  useEffect(() => {
    if (templateId) loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateId]);

  const loadData = async () => {
    if (!templateId) return;
    try {
      setLoading(true);
      const [t, allAgents, allWorkspaces] = await Promise.all([getTemplate(templateId), listAgents(), listWorkspaces()]);
      setTemplate(t);
      setName(t.name);
      setDescription(t.description || '');
      setGreeting(t.greeting || '');
      setSttProvider(t.sttProvider);
      setTtsProvider(t.ttsProvider);
      setLlmModel(t.llmModel);
      setVoiceMode(t.voiceMode || 'pipeline');
      setBaseSystemPrompt(t.baseSystemPrompt);
      setLanguage(t.language);
      setIsPublic(t.isPublic);
      setCustomFields(t.customFields || []);
      setAgents(allAgents.filter((a) => a.templateId === templateId));
      setWorkspaces(allWorkspaces);
      const linkedIds = allWorkspaces.filter((w) => w.templateIds.includes(templateId)).map((w) => w.id);
      setSelectedWorkspaceIds(linkedIds);
      setInitialWorkspaceIds(linkedIds);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!templateId) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const updated = await updateTemplate(templateId, {
        name, description: description || undefined, greeting: greeting || undefined,
        sttProvider, ttsProvider, llmModel, baseSystemPrompt, language, voiceMode, isPublic,
        customFields: customFields.filter((f) => f.key && f.label),
      });
      setTemplate(updated);

      // Update workspace associations
      const added = selectedWorkspaceIds.filter((id) => !initialWorkspaceIds.includes(id));
      const removed = initialWorkspaceIds.filter((id) => !selectedWorkspaceIds.includes(id));
      for (const wsId of added) {
        const ws = workspaces.find((w) => w.id === wsId);
        if (ws) await updateWorkspace(wsId, { templateIds: [...ws.templateIds, templateId!] });
      }
      for (const wsId of removed) {
        const ws = workspaces.find((w) => w.id === wsId);
        if (ws) await updateWorkspace(wsId, { templateIds: ws.templateIds.filter((tid) => tid !== templateId) });
      }
      setInitialWorkspaceIds(selectedWorkspaceIds);

      setSuccess('Saved');
      setTimeout(() => setSuccess(null), 2000);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!templateId || !window.confirm('Delete this template and all its agents?')) return;
    await deleteTemplate(templateId);
    navigate('/templates');
  };

  const toggleWorkspace = (wsId: string) => {
    setSelectedWorkspaceIds((prev) => prev.includes(wsId) ? prev.filter((id) => id !== wsId) : [...prev, wsId]);
  };

  const addField = () => setCustomFields([...customFields, { key: '', label: '', type: 'text', required: false }]);
  const updateField = (i: number, u: Partial<CustomFieldDef>) => setCustomFields(customFields.map((f, j) => j === i ? { ...f, ...u } : f));
  const removeField = (i: number) => setCustomFields(customFields.filter((_, j) => j !== i));

  if (loading) return <div style={s.container}><div style={s.muted}>Loading...</div></div>;
  if (!template) return <div style={s.container}><div style={s.error}>Template not found</div></div>;

  return (
    <div style={s.container}>
      <button style={s.backBtn} onClick={() => navigate('/templates')}>&larr; Back to templates</button>
      <h2 style={s.heading}>{template.name}</h2>

      {/* Settings */}
      <div style={s.section}>
        <h3 style={s.sub}>Settings</h3>
        <div style={s.form}>
          <label style={s.label}>Name <input style={s.input} value={name} onChange={(e) => setName(e.target.value)} /></label>
          <label style={s.label}>Description <input style={s.input} value={description} onChange={(e) => setDescription(e.target.value)} /></label>
          <label style={s.label}>Greeting (Twilio) <input style={s.input} value={greeting} onChange={(e) => setGreeting(e.target.value)} /></label>
          <div style={s.row}>
            <label style={s.label}>Language <select style={s.select} value={language} onChange={(e) => setLanguage(e.target.value)}>{LANGUAGES.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}</select></label>
            <label style={s.label}>LLM <select style={s.select} value={llmModel} onChange={(e) => setLlmModel(e.target.value)}>{LLM_MODELS.map((m) => <option key={m} value={m}>{m}</option>)}</select></label>
          </div>
          <div style={s.row}>
            <label style={s.label}>STT <select style={s.select} value={sttProvider} onChange={(e) => setSttProvider(e.target.value)}>{STT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}</select></label>
            <label style={s.label}>TTS <select style={s.select} value={ttsProvider} onChange={(e) => setTtsProvider(e.target.value)}>{TTS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}</select></label>
          </div>
          <label style={s.label}>Voice mode <select style={s.select} value={voiceMode} onChange={(e) => setVoiceMode(e.target.value as 'pipeline' | 'realtime')}><option value="pipeline">Pipeline (STT → LLM → TTS, provider metrics)</option><option value="realtime">Realtime (OpenAI speech-to-speech, low latency; STT/TTS ignored)</option></select></label>
          <label style={s.label}>System Prompt <textarea style={s.textarea} value={baseSystemPrompt} onChange={(e) => setBaseSystemPrompt(e.target.value)} rows={5} /></label>
          <label style={{ ...s.label, flexDirection: 'row', alignItems: 'center', gap: 8 }}><input type="checkbox" checked={isPublic} onChange={(e) => setIsPublic(e.target.checked)} /> Public</label>

          {/* Custom Fields */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <span style={{ fontSize: 13, color: '#555', fontWeight: 600 }}>Custom Fields ({customFields.length})</span>
              <button style={s.smallBtn} onClick={addField}>+ Add</button>
            </div>
            {customFields.map((f, i) => (
              <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 4, alignItems: 'center' }}>
                <input style={{ ...s.input, flex: 1, padding: '4px 8px', fontSize: 12 }} placeholder="key" value={f.key} onChange={(e) => updateField(i, { key: e.target.value })} />
                <input style={{ ...s.input, flex: 1, padding: '4px 8px', fontSize: 12 }} placeholder="Label" value={f.label} onChange={(e) => updateField(i, { label: e.target.value })} />
                <select style={{ ...s.select, padding: '4px 8px', fontSize: 12 }} value={f.type} onChange={(e) => updateField(i, { type: e.target.value as any })}><option value="text">text</option><option value="textarea">textarea</option><option value="select">select</option></select>
                <label style={{ fontSize: 11 }}><input type="checkbox" checked={f.required} onChange={(e) => updateField(i, { required: e.target.checked })} /> req</label>
                <button style={s.delBtn} onClick={() => removeField(i)}>x</button>
              </div>
            ))}
          </div>

          {/* Workspaces */}
          {workspaces.length > 0 && (
            <div>
              <span style={{ fontSize: 13, color: '#555', fontWeight: 600 }}>Workspaces</span>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
                {workspaces.map((w) => (
                  <button
                    key={w.id}
                    style={{
                      padding: '4px 10px', borderRadius: 6, fontSize: 12, cursor: 'pointer',
                      border: '1px solid',
                      ...(selectedWorkspaceIds.includes(w.id)
                        ? { background: '#4f46e5', color: '#fff', borderColor: '#4f46e5' }
                        : { background: '#fff', color: '#555', borderColor: '#ddd' }),
                    }}
                    onClick={() => toggleWorkspace(w.id)}
                  >
                    {w.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          {error && <div style={s.error}>{error}</div>}
          {success && <div style={s.success}>{success}</div>}
          <div style={{ display: 'flex', gap: 12 }}>
            <button style={s.saveBtn} onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
            <button style={s.delBtn} onClick={handleDelete}>Delete Template</button>
          </div>
        </div>
      </div>

      {/* Agents using this template */}
      <div style={s.section}>
        <h3 style={s.sub}>Agents ({agents.length})</h3>
        {agents.length === 0 ? <div style={s.muted}>No agents created from this template</div> : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {agents.map((a) => (
              <div key={a.id} style={s.agentCard} onClick={() => navigate(`/agents/${a.id}`)}>
                <strong>{a.name}</strong>
                <code style={s.apiKey}>{a.apiKey}</code>
              </div>
            ))}
          </div>
        )}
      </div>

      <div style={s.section}>
        <div style={s.muted}>Template ID: {template.id}</div>
        <div style={s.muted}>Created: {new Date(template.createdAt).toLocaleString()}</div>
      </div>
    </div>
  );
}

const s: Record<string, React.CSSProperties> = {
  container: { padding: 20, flex: 1, overflowY: 'auto' },
  backBtn: { background: 'none', border: 'none', color: '#4f46e5', fontSize: 14, cursor: 'pointer', padding: 0, marginBottom: 12 },
  heading: { margin: '0 0 16px', fontSize: 20, color: '#1a1a1a' },
  sub: { margin: '0 0 10px', fontSize: 15, fontWeight: 600, color: '#1a1a1a' },
  section: { marginBottom: 20, padding: '14px 18px', border: '1px solid #e5e7eb', borderRadius: 10, background: '#fafafa' },
  form: { display: 'flex', flexDirection: 'column', gap: 10 },
  row: { display: 'flex', gap: 10 },
  label: { display: 'flex', flexDirection: 'column', gap: 3, fontSize: 13, color: '#555', flex: 1 },
  input: { padding: '7px 10px', borderRadius: 8, border: '1px solid #ddd', fontSize: 14, outline: 'none' },
  select: { padding: '7px 10px', borderRadius: 8, border: '1px solid #ddd', fontSize: 14, background: '#fff' },
  textarea: { padding: '7px 10px', borderRadius: 8, border: '1px solid #ddd', fontSize: 14, fontFamily: 'inherit', resize: 'vertical', outline: 'none' },
  saveBtn: { padding: '8px 18px', borderRadius: 8, border: 'none', background: '#4f46e5', color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  smallBtn: { padding: '3px 8px', borderRadius: 6, border: '1px solid #ddd', background: '#fff', fontSize: 11, cursor: 'pointer', color: '#555' },
  delBtn: { padding: '4px 12px', borderRadius: 6, border: '1px solid #fca5a5', background: '#fff', color: '#dc2626', fontSize: 12, cursor: 'pointer' },
  error: { padding: '8px 12px', background: '#fff0f0', border: '1px solid #fcc', borderRadius: 8, color: '#c00', fontSize: 13 },
  success: { padding: '8px 12px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, color: '#16a34a', fontSize: 13 },
  muted: { color: '#888', fontSize: 13 },
  agentCard: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, cursor: 'pointer' },
  apiKey: { fontSize: 11, fontFamily: 'monospace', color: '#666', background: '#e2e8f0', padding: '2px 6px', borderRadius: 4 },
};
